import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios, { AxiosRequestConfigExtended } from '../axios';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('No reset token provided');
      setLoading(false);
      return;
    }

    // Token is present, we can proceed
    setTokenValid(true);
    setLoading(false);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      const token = searchParams.get('token');
      if (!token) {
        setError('No reset token provided');
        return;
      }

      const response = await axios.post(
        '/user/reset-password',
        {
          token,
          new_password: password,
        },
        {
          _bypassInterceptor: true,
        } as AxiosRequestConfigExtended
      );

      if (response.status === 200) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err: any) {
      switch (err.response?.status) {
        case 400:
          setError('Password is too short (minimum 8 characters)');
          break;
        case 401:
          setError(
            'Invalid or expired reset token. Please request a new reset link.'
          );
          break;
        case 404:
          setError('User not found. Please request a new reset link.');
          break;
        case 500:
          setError('Server error occurred. Please try again later.');
          break;
        default:
          setError(
            err.response?.data?.message ||
              err.response?.data ||
              'An error occurred while resetting password'
          );
      }
    }
  };

  if (loading) {
    return (
      <Container component='main' maxWidth='xs'>
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!tokenValid) {
    return (
      <Container component='main' maxWidth='xs'>
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Alert severity='error'>
            Invalid or expired reset token. Please request a new password reset
            link.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container component='main' maxWidth='xs'>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component='h1' variant='h5'>
            Reset Password
          </Typography>
          {success ? (
            <Alert severity='success' sx={{ mt: 2 }}>
              Password reset successful! Redirecting to login...
            </Alert>
          ) : (
            <Box component='form' onSubmit={handleSubmit} sx={{ mt: 3 }}>
              {error && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                margin='normal'
                required
                fullWidth
                name='password'
                label='New Password'
                type='password'
                id='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                margin='normal'
                required
                fullWidth
                name='confirmPassword'
                label='Confirm New Password'
                type='password'
                id='confirmPassword'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Button
                type='submit'
                fullWidth
                variant='contained'
                sx={{ mt: 3, mb: 2 }}
              >
                Reset Password
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
